// ---- Data-driven structures ----
// Interprets a content/structures/*.json document (StructureDoc, see the
// content schema) into StructureBuildCtx calls — the EXACT helpers coded
// buildings (castle.ts) use, so collision, walkable floors, cutaway
// tagging, and the consolidation pass all come free by construction. This is
// the structure-side twin of src/model/parts.ts.
//
// THREE-but-DOM-free and LOAD-BEARING for the headless server: buildWorld runs
// this when it builds placed structures for NPC physics — never import DOM here.
//
// Architect composition for free-standing houses:
//   base → mass → (floors/stairs) → gableRoof → chimney → furnish
//
// - `mass` = continuous sharp shell (overlapping full-face walls, openings,
//   cornice, ceiling). Prefer this over stitching rooms for cottages.
// - `room` = legacy / insert macro (mitred walls by default; shell/plaster =
//   continuous overlap; logs = cabin ribs). Upper-floor slabs auto-inset by
//   wallT·0.85 (the z-fight rule from structure/CLAUDE.md).
// - `gableRoof` finish "shingle" (default, BC) | "mass" (cohesive crown) |
//   "curve" (storybook cambered shell + soft ridge/barge/eave trim).

import type { StructureBuildCtx } from "./define.ts";
import type { StructureDoc, StructureOp } from "../content/schemas.ts";
import { resolveStructureDoc } from "./params.ts";
import { doorway, windowPane } from "./openings.ts";
import { stoneCourses, stoneSteps, timberFraming, type Side } from "./trim.ts";
import { FURNITURE } from "./furniture/index.ts";
import { resolvePalette, colorOf, interpretParts, parseHex, type Part } from "../model/parts.ts";
import { shade } from "../color.ts";
import { buildCurveRoof, strokePath } from "./curve-roof.ts";

type RoomOp = Extract<StructureOp, { op: "room" }>;
type MassOp = Extract<StructureOp, { op: "mass" }>;
type GableRoofOp = Extract<StructureOp, { op: "gableRoof" }>;
type BaseOp = Extract<StructureOp, { op: "base" }>;
type PlinthOp = Extract<StructureOp, { op: "plinth" }>;
type ChimneyOp = Extract<StructureOp, { op: "chimney" }>;

/** A door opening on a room side, read by logTreatment to break ribs around it:
 *  `at` = center along the wall run, `half` = half-width, `h` = opening height. */
type DoorGap = { at: number; half: number; h: number };

/** Board flooring laid proud of a slab (visual only; the slab is the walkable
 *  surface). Rows run along z, tinted per-row. Shared by every room's plankFloor. */
function plankFloor(
  c: StructureBuildCtx, minX: number, maxX: number, minZ: number, maxZ: number,
  y: number, baseCol: number, width: number,
): void {
  const d = maxZ - minZ;
  const rows = Math.max(1, Math.round(d / width));
  const tw = d / rows, gap = 0.12;
  // asFloor so boards stay with the slab when cutaway leaves upper decks visible.
  const lay = () => {
    for (let r = 0; r < rows; r++) {
      const cz = minZ + (r + 0.5) * tw;
      const col = shade(baseCol, ((r * 37) % 7 - 3) * 0.018);
      c.box((minX + maxX) / 2, y + 0.03, cz, maxX - minX, 0.08, tw - gap, col);
    }
  };
  if (c.asFloor) c.asFloor(lay);
  else lay();
}

/** Log-cabin wall treatment: round corner posts (crisscross overhang) + stacked
 *  horizontal half-log ribs banded over each exterior face. Purely decorative —
 *  the flat structural wall the room macro already emitted carries the collision.
 *  Each face's ribs are `shell()`-wrapped with that wall's outward normal (like
 *  windowPane), so they wall-fade WITH the wall they veneer instead of hanging
 *  opaque in front of it; only the corner posts (which straddle two faces, so
 *  have no single normal) are `keep()`-tagged. Ribs split around a door. */
function logTreatment(c: StructureBuildCtx, o: RoomOp, doors: Map<string, DoorGap>, logCol: number): void {
  const top = o.baseY + o.wallH;
  const r = 0.28;                 // rib radius
  const rp = o.wallT * 0.72;      // corner-post radius
  const step = r * 1.8;
  c.keep(() => {
    for (const [px, pz] of [
      [o.minX, o.minZ], [o.maxX, o.minZ], [o.minX, o.maxZ], [o.maxX, o.maxZ],
    ] as const) {
      c.cylinder(px, o.baseY, pz, rp, rp, o.wallH + 0.25, shade(logCol, -0.14));
    }
  });

  const faces = [
    { side: "north", axis: "x" as const, faceCoord: o.minZ, lo: o.minX, hi: o.maxX, nx: 0 as const, nz: -1 as const },
    { side: "south", axis: "x" as const, faceCoord: o.maxZ, lo: o.minX, hi: o.maxX, nx: 0 as const, nz: 1 as const },
    { side: "west", axis: "z" as const, faceCoord: o.minX, lo: o.minZ, hi: o.maxZ, nx: -1 as const, nz: 0 as const },
    { side: "east", axis: "z" as const, faceCoord: o.maxX, lo: o.minZ, hi: o.maxZ, nx: 1 as const, nz: 0 as const },
  ];
  for (const f of faces) {
    const door = doors.get(f.side);
    c.shell(f.nx, f.nz, () => {
      for (let y = o.baseY + r; y < top - r * 0.5; y += step) {
        const rowCol = shade(logCol, ((Math.round(y * 10) % 5) - 2) * 0.02);
        const segs: [number, number][] = [];
        if (door && y < o.baseY + door.h + r) {
          const g0 = door.at - door.half, g1 = door.at + door.half;
          if (g0 > f.lo + 0.05) segs.push([f.lo, g0]);
          if (g1 < f.hi - 0.05) segs.push([g1, f.hi]);
        } else {
          segs.push([f.lo, f.hi]);
        }
        for (const [a, b] of segs) {
          const len = b - a;
          if (len <= 0.1) continue;
          const mid = (a + b) / 2;
          if (f.axis === "x") c.tube(mid, y, f.faceCoord, r, len, rowCol, "x");
          else c.tube(f.faceCoord, y, mid, r, len, rowCol, "z");
        }
      }
    });
  }
}

/**
 * GABLE ROOF — building crown.
 *
 * `finish:"shingle"` (default — backward compatible): classic many-course
 * alternating steps (existing cottages/log houses keep their layered look).
 *
 * `finish:"mass"`: continuous roof volume — sharp overlapping planes, one
 * material, solid gable-end fill, eave plate + fascia. Opt in for new styles.
 *
 * `finish:"curve"`: storybook cambered shell (concept B) — low-poly curved
 * halves + gable fills via `c.mesh`, soft ridge/barge/eave rolls via rod/ball.
 *
 * All keep() — never collides, never wall-fades.
 */
function gableRoof(c: StructureBuildCtx, col: (k: string) => number, o: GableRoofOp): void {
  const finish = o.finish ?? "shingle";
  if (finish === "curve") {
    curveGableRoof(c, col, o);
    return;
  }
  const massy = finish === "mass";
  const steps = o.steps ?? (massy ? 4 : 7);
  const eave = o.eave ?? 0.9;
  const stepH = o.height / steps;
  const color = col(o.color);
  const colorDk = o.colorDk ? col(o.colorDk) : shade(color, -0.1);
  const ridgeColor = o.ridgeColor ? col(o.ridgeColor) : shade(color, -0.14);
  const softRidge = o.softRidge ?? massy;
  const softEaves = o.softEaves ?? massy;
  const cx = (o.minX + o.maxX) / 2, cz = (o.minZ + o.maxZ) / 2;
  const hx = (o.maxX - o.minX) / 2, hz = (o.maxZ - o.minZ) / 2;
  const ridgeY = o.baseY + o.height;
  // Standable pitches (gravity plan): one walk RAMP per side, eave → ridge —
  // land on the roof and walk its pitch. The analytic plane sits within a
  // course-step of the stepped visual; gable ends stay walk-off edges.
  roofRamps(c, o, eave);
  // Prefer solid (sharp) when available; fall back to box for partial mocks.
  const solid = (x: number, y: number, z: number, w: number, h: number, d: number, coln: number) => {
    if (c.solid) c.solid(x, y, z, w, h, d, coln);
    else c.box(x, y, z, w, h, d, coln);
  };

  c.keep(() => {
    if (o.ridgeAxis === "z") {
      const baseHX = hx + eave, ridgeHX = 0.45;
      const roofLen = (hz + eave) * 2;
      const z0 = cz - roofLen / 2, z1 = cz + roofLen / 2;

      // Continuous eave plate — mass finish only (shingle keeps the classic
      // stepped courses without a full lid slab — visual BC for live cottages).
      if (massy) {
        solid(cx, o.baseY + 0.1, cz, baseHX * 2 + 0.15, 0.22, roofLen + 0.15, shade(color, -0.04));
      }

      // Body courses — heavy vertical overlap so they fuse into one mass.
      // Mass finish: same color throughout (no zebra striping).
      const thick = massy ? stepH * 1.45 : stepH * 1.06;
      for (let k = 0; k < steps; k++) {
        const t = steps === 1 ? 0 : k / (steps - 1);
        const y = o.baseY + k * stepH + stepH / 2;
        const w = (baseHX - (baseHX - ridgeHX) * t) * 2;
        const courseCol = massy ? color : (k % 2 ? colorDk : color);
        solid(cx, y, cz, w, thick, roofLen, courseCol);
      }

      // 3) Solid gable ends (triangular fill under the pitch) — closes the roof
      //    as a volume, not a stack of open shelves.
      if (massy) {
        for (let k = 0; k < steps; k++) {
          const t = steps === 1 ? 0 : k / (steps - 1);
          const y = o.baseY + k * stepH + stepH / 2;
          const w = (baseHX - (baseHX - ridgeHX) * t) * 2;
          const th = stepH * 1.2;
          solid(cx, y, z0 + 0.08, w, th, 0.22, shade(color, -0.05));
          solid(cx, y, z1 - 0.08, w, th, 0.22, shade(color, -0.05));
        }
      }

      // 4) Ridge beam — continuous bar along the peak.
      solid(cx, ridgeY + 0.08, cz, ridgeHX * 2 + 0.35, 0.32, roofLen + 0.1, ridgeColor);
      if (softRidge) {
        const half = roofLen / 2;
        c.rod(cx, ridgeY + 0.22, cz - half, cx, ridgeY + 0.22, cz + half, 0.18, ridgeColor);
        c.ball(cx, ridgeY + 0.22, cz - half, 0.22, ridgeColor);
        c.ball(cx, ridgeY + 0.22, cz + half, 0.22, ridgeColor);
      }

      // 5) Eave fascia — sharp continuous lip (not four floating tubes).
      if (softEaves) {
        const fy = o.baseY + 0.05;
        const fh = 0.28;
        const halfW = baseHX, halfL = hz + eave;
        solid(cx, fy, cz - halfL, halfW * 2, fh, 0.18, ridgeColor);
        solid(cx, fy, cz + halfL, halfW * 2, fh, 0.18, ridgeColor);
        solid(cx - halfW, fy, cz, 0.18, fh, halfL * 2, ridgeColor);
        solid(cx + halfW, fy, cz, 0.18, fh, halfL * 2, ridgeColor);
      }
    } else {
      // ridge along x — mirror of z case
      const baseHZ = hz + eave, ridgeHZ = 0.45;
      const roofLen = (hx + eave) * 2;
      const x0 = cx - roofLen / 2, x1 = cx + roofLen / 2;

      if (massy) {
        solid(cx, o.baseY + 0.1, cz, roofLen + 0.15, 0.22, baseHZ * 2 + 0.15, shade(color, -0.04));
      }

      const thick = massy ? stepH * 1.45 : stepH * 1.06;
      for (let k = 0; k < steps; k++) {
        const t = steps === 1 ? 0 : k / (steps - 1);
        const y = o.baseY + k * stepH + stepH / 2;
        const d = (baseHZ - (baseHZ - ridgeHZ) * t) * 2;
        const courseCol = massy ? color : (k % 2 ? colorDk : color);
        solid(cx, y, cz, roofLen, thick, d, courseCol);
      }

      if (massy) {
        for (let k = 0; k < steps; k++) {
          const t = steps === 1 ? 0 : k / (steps - 1);
          const y = o.baseY + k * stepH + stepH / 2;
          const d = (baseHZ - (baseHZ - ridgeHZ) * t) * 2;
          const th = stepH * 1.2;
          solid(x0 + 0.08, y, cz, 0.22, th, d, shade(color, -0.05));
          solid(x1 - 0.08, y, cz, 0.22, th, d, shade(color, -0.05));
        }
      }

      solid(cx, ridgeY + 0.08, cz, roofLen + 0.1, 0.32, ridgeHZ * 2 + 0.35, ridgeColor);
      if (softRidge) {
        const half = roofLen / 2;
        c.rod(cx - half, ridgeY + 0.22, cz, cx + half, ridgeY + 0.22, cz, 0.18, ridgeColor);
        c.ball(cx - half, ridgeY + 0.22, cz, 0.22, ridgeColor);
        c.ball(cx + half, ridgeY + 0.22, cz, 0.22, ridgeColor);
      }

      if (softEaves) {
        const fy = o.baseY + 0.05;
        const fh = 0.28;
        const halfL = hx + eave, halfD = baseHZ;
        solid(cx, fy, cz - halfD, halfL * 2, fh, 0.18, ridgeColor);
        solid(cx, fy, cz + halfD, halfL * 2, fh, 0.18, ridgeColor);
        solid(cx - halfL, fy, cz, 0.18, fh, halfD * 2, ridgeColor);
        solid(cx + halfL, fy, cz, 0.18, fh, halfD * 2, ridgeColor);
      }
    }
  });
}

/** The standable roof-pitch ramps (gravity plan) — shared by both gable roof
 *  finishes: two walk ramps, eave → ridge, one per side of the ridge line. The
 *  curve finish uses the CHORD (camber float ≤ ~0.3u — acceptable v1). */
function roofRamps(c: StructureBuildCtx, o: GableRoofOp, eave: number): void {
  if (!c.standRamp) return; // optional hook (partial test mocks)
  const cx = (o.minX + o.maxX) / 2, cz = (o.minZ + o.maxZ) / 2;
  const ridgeY = o.baseY + o.height;
  if (o.ridgeAxis === "z") {
    // Ridge runs along z at x = cx; pitches slope along x.
    c.standRamp(o.minX - eave, cx, o.minZ - eave, o.maxZ + eave, o.baseY, ridgeY, "x", "min");
    c.standRamp(cx, o.maxX + eave, o.minZ - eave, o.maxZ + eave, o.baseY, ridgeY, "x", "max");
  } else {
    // Ridge runs along x at z = cz; pitches slope along z.
    c.standRamp(o.minX - eave, o.maxX + eave, o.minZ - eave, cz, o.baseY, ridgeY, "z", "min");
    c.standRamp(o.minX - eave, o.maxX + eave, cz, o.maxZ + eave, o.baseY, ridgeY, "z", "max");
  }
}

/**
 * Storybook curved gable (concept B).
 * Architecture:
 *   1. Two smooth half-shells + undersides (freeform mesh, soft toon ramp)
 *   2. Gable-end fills under the curved pitch
 *   3. Soft ridge beam following the camber (UNIT_CYL rods — consolidatable)
 *   4. Soft bargeboards on both gable edges
 *   5. Soft eave rolls along both eaves
 */
function curveGableRoof(c: StructureBuildCtx, col: (k: string) => number, o: GableRoofOp): void {
  const eave = o.eave ?? 1.2;
  roofRamps(c, o, eave); // standable pitches (chord of the camber)
  const color = col(o.color);
  const colorDk = o.colorDk ? col(o.colorDk) : shade(color, -0.12);
  const ridgeColor = o.ridgeColor ? col(o.ridgeColor) : shade(color, -0.18);
  const softRidge = o.softRidge ?? true;
  const softEaves = o.softEaves ?? true;
  // Defaults tuned to concept-B proportions on a ~cottage-scale mass.
  const camber = o.camber ?? Math.min(0.55, o.height * 0.12);
  const kick = o.kick ?? Math.min(0.7, o.height * 0.16);
  const pitchSegs = o.pitchSegs ?? 6;
  const alongSegs = o.alongSegs ?? 6;

  const built = buildCurveRoof({
    ridgeAxis: o.ridgeAxis,
    minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.maxZ,
    baseY: o.baseY, height: o.height, eave,
    camber, kick, pitchSegs, alongSegs, shellT: 0.38,
  });

  c.keep(() => {
    const withLayer = (name: string, fn: () => void) => {
      if (c.layer) c.layer(name, fn);
      else fn();
    };
    // Freeform path is optional on partial test mocks.
    if (c.mesh) {
      const gableCol = shade(color, -0.04);
      // Soffit / fill / plates: dark wood-shadow so underside never reads as sky.
      const soffitCol = shade(colorDk, -0.22);
      withLayer("roof", () => {
        for (const g of built.shells) c.mesh!(g, color, { smooth: true });
        for (const g of built.unders) c.mesh!(g, soffitCol, { smooth: true });
        for (const g of built.gables) c.mesh!(g, gableCol, { smooth: true });
        for (const g of built.eaveCaps) c.mesh!(g, shade(color, -0.1), { smooth: true });
      });
      withLayer("fill", () => {
        for (const g of built.plates) c.mesh!(g, soffitCol, { smooth: true });
        // Solid under each half — closes long-side eave views into the attic.
        for (const g of built.fills) c.mesh!(g, soffitCol, { smooth: true });
      });
    }

    withLayer("trim", () => {
      if (softRidge) {
        // Fat rolled ridge following the camber (concept B's thick ridge beam).
        strokePath(built.ridge, 0.26, ridgeColor, c.rod.bind(c), c.ball.bind(c), 1.25);
      }
      if (softEaves) {
        // Soft eave rolls + bargeboards on both gables.
        strokePath(built.eaveL, 0.18, ridgeColor, c.rod.bind(c), c.ball.bind(c), 1.15);
        strokePath(built.eaveR, 0.18, ridgeColor, c.rod.bind(c), c.ball.bind(c), 1.15);
        strokePath(built.barge0, 0.16, ridgeColor, c.rod.bind(c), c.ball.bind(c), 1.2);
        strokePath(built.barge1, 0.16, ridgeColor, c.rod.bind(c), c.ball.bind(c), 1.2);
      }
    });
  });
}

/**
 * BASE — foundation as ONE solid pad (architect: the footing is a mass, not
 * four wall bands). Optional stoop / multi-step stairs. `finish:"courses"`
 * adds concept-B irregular stone block veneer (src/structure/trim.ts).
 */
function base(c: StructureBuildCtx, col: (k: string) => number, o: BaseOp): void {
  const h = o.height;
  const outset = o.outset ?? 0.45;
  const baseY = o.baseY ?? -h;
  const color = col(o.color);
  const colorDk = o.colorDk ? col(o.colorDk) : shade(color, -0.14);
  const minX = o.minX - outset, maxX = o.maxX + outset;
  const minZ = o.minZ - outset, maxZ = o.maxZ + outset;
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  const w = maxX - minX, d = maxZ - minZ;
  const topY = baseY + h; // pad top (= door sill / mass floor by convention)
  const nSteps = o.steps ?? 1;
  // Foundation never wall-fades — it's ground mass. Sharp pad coheres with
  // UNIT_SHARP walls; rounded box left only for the soft shadow lip.
  const solid = (x: number, y: number, z: number, ww: number, hh: number, dd: number, coln: number) => {
    if (c.solid) c.solid(x, y, z, ww, hh, dd, coln);
    else c.box(x, y, z, ww, hh, dd, coln);
  };
  c.keep(() => {
    // Shadow lip under the pad (soft).
    c.box(cx, baseY - 0.08, cz, w + 0.2, 0.16, d + 0.2, colorDk);
    // THE pad — single sharp solid.
    solid(cx, baseY + h / 2, cz, w, h, d, color);
    // Single-slab stoop when steps ≤ 1 (BC). Multi-step uses stoneSteps below.
    if (o.stoop && nSteps <= 1) {
      const depth = o.stoopDepth ?? 1.2;
      const sw = o.stoopWidth ?? Math.min(w, d) * 0.55;
      const sh = h * 0.55;
      if (o.stoop === "west") {
        solid(minX - depth / 2, baseY + sh / 2, cz, depth, sh, sw, color);
      } else if (o.stoop === "east") {
        solid(maxX + depth / 2, baseY + sh / 2, cz, depth, sh, sw, color);
      } else if (o.stoop === "north") {
        solid(cx, baseY + sh / 2, minZ - depth / 2, sw, sh, depth, color);
      } else {
        solid(cx, baseY + sh / 2, maxZ + depth / 2, sw, sh, depth, color);
      }
    }
  });

  // Multi-step stone stair on the stoop face (concept B entry).
  if (o.stoop && nSteps > 1) {
    const depth = o.stoopDepth ?? 1.6;
    const sw = o.stoopWidth ?? Math.min(w, d) * 0.55;
    const face =
      o.stoop === "west" ? minX
      : o.stoop === "east" ? maxX
      : o.stoop === "north" ? minZ
      : maxZ;
    const along = o.stoop === "west" || o.stoop === "east" ? cz : cx;
    stoneSteps(c, o.stoop as Side, face, along, {
      steps: nSteps,
      width: sw,
      totalDepth: depth,
      topY,
      bottomY: Math.min(0, topY - 0.85),
      color,
      colorDk,
    });
  }

  // Irregular stone course veneer. The solid pad sits mostly under grade
  // (top ≈ y=0); courses wrap the AUTHORIED wall footprint and rise ~1u ABOVE
  // the pad top so they read as a raised plinth against plaster (concept B /
  // classic foundation band) — not buried in the footing.
  if (o.finish === "courses") {
    const bandH = Math.max(1.0, h * 0.55 + 0.65);
    const gapHalf = o.stoop
      ? (o.stoopWidth ?? Math.min(w, d) * 0.55) / 2 + 0.2
      : 0;
    // Wall-aligned rect (no pad outset) so courses sit under the plaster shell.
    stoneCourses(c, o.minX, o.maxX, o.minZ, o.maxZ, {
      y0: topY - 0.12,
      height: bandH,
      courses: o.courses ?? 2,
      depth: 0.32,
      color,
      colorDk,
      gap: o.stoop
        ? {
          side: o.stoop as Side,
          at: (o.stoop === "west" || o.stoop === "east") ? cz : cx,
          half: gapHalf,
        }
        : undefined,
      seed: Math.round((o.minX + o.maxX + o.minZ + o.maxZ) * 10),
    });
  }
}

/** Legacy plinth → solid base pad (band fields ignored; outset/height kept). */
function plinth(c: StructureBuildCtx, col: (k: string) => number, o: PlinthOp): void {
  base(c, col, {
    op: "base",
    minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.maxZ,
    baseY: o.baseY, height: o.height, outset: o.outset ?? 0.4,
    color: o.color, colorDk: o.colorDk,
  });
}

/** Exterior chimney stack + cap — roof appendage, keep()-tagged. */
function chimney(c: StructureBuildCtx, col: (k: string) => number, o: ChimneyOp): void {
  const w = o.w ?? 1.5, d = o.d ?? 1.5;
  const color = col(o.color);
  const cap = o.capColor ? col(o.capColor) : shade(color, -0.14);
  const solid = (x: number, y: number, z: number, ww: number, hh: number, dd: number, coln: number) => {
    if (c.solid) c.solid(x, y, z, ww, hh, dd, coln);
    else c.box(x, y, z, ww, hh, dd, coln);
  };
  c.keep(() => {
    // Sharp stack coheres with mass roof planes; soft caps stay on ball() when soft.
    solid(o.x, o.baseY + o.h / 2, o.z, w, o.h, d, color);
    solid(o.x, o.baseY + o.h + 0.18, o.z, w + 0.35, 0.36, d + 0.35, cap);
    if (o.soft) {
      const r = Math.min(w, d) * 0.22;
      const y = o.baseY + o.h + 0.18;
      for (const [dx, dz] of [
        [-w / 2, -d / 2], [w / 2, -d / 2], [-w / 2, d / 2], [w / 2, d / 2],
      ] as const) {
        c.ball(o.x + dx, y, o.z + dz, r, cap);
      }
    }
  });
}

/**
 * MASS — primary building volume.
 * Continuous sharp-edged shell (wall() uses UNIT_SHARP so coplanar flanks+lintels
 * join as one plane). Overlapping full-face walls bury corners. Optional cornice
 * crowns the mass for the roof to sit on. Logs finish is the only decorative
 * wall treatment that adds separate posts/ribs.
 */
function mass(c: StructureBuildCtx, col: (k: string) => number, o: MassOp): void {
  const t = o.wallT;
  const top = o.baseY + o.wallH;
  const wallCol = col(o.wallColor);
  const floorCol = col(o.floorColor);
  const finish = o.finish ?? "shell";
  const logs = finish === "logs";

  // Ground floor + a `base` pad both land at y≈0 by convention (base tops at
  // baseY+height with default baseY=−height). A coplanar wood slab on a masonry
  // pad z-fights, and plank gaps flash foundation color. Lift the walk surface a
  // hair so wood owns the interior plane; exterior pad ledge stays flush with
  // the wall bottom. Upper storeys (baseY > 0) need no lift.
  const floorLift = o.baseY <= 0.05 ? 0.06 : 0;
  const floorY = o.baseY + floorLift;
  // Sink walls slightly into the foundation pad so the wall bottom face isn't
  // coplanar with the pad top (stair-step z-fight on the exterior ledge).
  const wallSink = o.baseY <= 0.05 ? 0.06 : 0;
  const wallBase = o.baseY - wallSink;
  const wallH = o.wallH + wallSink;
  const wallTop = o.baseY + o.wallH;
  // Inset floor from the outer wall face so the deck doesn't pierce the shell
  // (storey-split walls + visible upper floors were z-fighting at the join).
  // Ground: was 0 (tucked under walls); always keep ≥ ~half wallT clearance.
  const floorInset = o.baseY > 0.05 ? t * 0.85 : t * 0.55;
  c.floorSlab(
    o.minX + floorInset, o.maxX - floorInset, o.minZ + floorInset, o.maxZ - floorInset,
    floorY, o.floorThickness ?? 0.3, floorCol,
  );
  if (o.plankFloor) {
    plankFloor(c, o.minX + floorInset, o.maxX - floorInset, o.minZ + floorInset, o.maxZ - floorInset,
      floorY, floorCol, o.plankWidth ?? 1.1);
  }

  // Continuous shell: every face runs full length (corners overlap, same color).
  // Logs keep mitre so posts sit cleanly at the join.
  const rects = logs
    ? {
      north: { minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.minZ + t },
      south: { minX: o.minX, maxX: o.maxX, minZ: o.maxZ - t, maxZ: o.maxZ },
      west: { minX: o.minX, maxX: o.minX + t, minZ: o.minZ + t, maxZ: o.maxZ - t },
      east: { minX: o.maxX - t, maxX: o.maxX, minZ: o.minZ + t, maxZ: o.maxZ - t },
    }
    : {
      north: { minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.minZ + t },
      south: { minX: o.minX, maxX: o.maxX, minZ: o.maxZ - t, maxZ: o.maxZ },
      west: { minX: o.minX, maxX: o.minX + t, minZ: o.minZ, maxZ: o.maxZ },
      east: { minX: o.maxX - t, maxX: o.maxX, minZ: o.minZ, maxZ: o.maxZ },
    };

  const doorGaps = new Map<string, DoorGap>();
  const frameCol = o.frameColor ? col(o.frameColor) : undefined;
  const useTimber = o.timber === true;
  const winStyle = useTimber ? "heavy" as const : "slim" as const;
  let doorForTimber: { side: Side; at: number; w: number; h: number } | undefined;

  for (const side of ["north", "south", "west", "east"] as const) {
    const rect = rects[side];
    const { axis, nx, nz } = SIDES[side];
    const openings = (o.openings ?? []).filter((op) => op.side === side);
    const door = openings.find((op) => op.kind === "door");

    if (door) {
      const half = (door.w ?? 1.5) / 2, dh = door.h ?? 2.4;
      // Flanks use sunk wallBase; door box/leaf sit on the pad sill (o.baseY).
      doorway(c, axis, rect, door.at, half, dh, wallBase, wallTop, wallCol, frameCol, {
        style: useTimber ? "heavy" : "slim",
        leaf: useTimber,
        colliderMinY: o.baseY,
      });
      doorGaps.set(side, { at: door.at, half, h: dh });
      doorForTimber = { side, at: door.at, w: door.w ?? 1.5, h: dh };
    } else {
      c.wall(rect.minX, rect.maxX, rect.minZ, rect.maxZ, wallBase, wallH, wallCol);
    }

    for (const win of openings) {
      if (win.kind !== "window") continue;
      const faceCoord =
        side === "north" ? rect.minZ : side === "south" ? rect.maxZ
        : side === "west" ? rect.minX : rect.maxX;
      windowPane(c, axis, win.at, faceCoord, nx, nz, o.baseY + (win.cy ?? 1.8), {
        w: win.w, h: win.h,
        frame: frameCol,
        style: winStyle,
      });
    }
  }

  if (logs) {
    logTreatment(c, {
      op: "room",
      minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.maxZ,
      baseY: o.baseY, wallH: o.wallH, wallT: o.wallT,
      wallColor: o.wallColor, floorColor: o.floorColor,
      wallTreatment: "logs", logColor: o.logColor,
    }, doorGaps, o.logColor ? col(o.logColor) : wallCol);
  }

  // Storybook timber package (concept B): plates under eaves, corner posts,
  // diagonal braces, heavy external door surround. Lives in trim.ts.
  if (useTimber) {
    const timberCol = o.timberColor ? col(o.timberColor) : (frameCol ?? 0x4a3a2c);
    // Posts start above a typical course band so they don't sink into foundation.
    const postBaseY = o.baseY + (o.baseY <= 0.05 ? 0.95 : 0);
    const runTimber = () => timberFraming(c, o.minX, o.maxX, o.minZ, o.maxZ, o.baseY, o.wallH, {
      color: timberCol,
      plate: true,
      posts: true,
      braces: true,
      door: doorForTimber,
      postBaseY,
    });
    if (c.layer) c.layer("trim", runTimber);
    else runTimber();
  }

  // Cornice — continuous wall-top crown (shell-tagged, sharp via wall path would
  // be better as wall strips; use wall so cutaway matches the shell).
  // Skip when timber plate already crowns the wall (double cornice reads busy).
  if (o.cornice !== false && !useTimber) {
    const ch = o.corniceH ?? 0.28;
    const cc = o.corniceColor ? col(o.corniceColor) : shade(wallCol, -0.06);
    const y0 = top - ch;
    // Full-run bands overlapping at corners — same continuous-mass rule.
    c.wall(o.minX - 0.06, o.maxX + 0.06, o.minZ - 0.06, o.minZ + t * 0.55, y0, ch, cc, { collide: false });
    c.wall(o.minX - 0.06, o.maxX + 0.06, o.maxZ - t * 0.55, o.maxZ + 0.06, y0, ch, cc, { collide: false });
    c.wall(o.minX - 0.06, o.minX + t * 0.55, o.minZ - 0.06, o.maxZ + 0.06, y0, ch, cc, { collide: false });
    c.wall(o.maxX - t * 0.55, o.maxX + 0.06, o.minZ - 0.06, o.maxZ + 0.06, y0, ch, cc, { collide: false });
  }

  if (o.ceiling !== false) {
    const cc = o.ceilingColor ? col(o.ceilingColor) : shade(wallCol, -0.08);
    c.ceiling(o.minX + t * 0.5, o.maxX - t * 0.5, o.minZ + t * 0.5, o.maxZ - t * 0.5, top, 0.32, cc);
  }
}

/** Which axis a room side's wall runs along, and its outward normal. */
const SIDES = {
  north: { axis: "x" as const, nx: 0 as const, nz: -1 as const },
  south: { axis: "x" as const, nx: 0 as const, nz: 1 as const },
  west: { axis: "z" as const, nx: -1 as const, nz: 0 as const },
  east: { axis: "z" as const, nx: 1 as const, nz: 0 as const },
};

function room(c: StructureBuildCtx, col: (k: string) => number, o: RoomOp): void {
  const t = o.wallT;
  const top = o.baseY + o.wallH;
  const wallCol = col(o.wallColor);
  // shell/plaster: continuous overlap (mass language). flat/logs: classic mitre.
  const continuous = o.wallTreatment === "plaster" || o.wallTreatment === "shell";

  const inset = o.baseY > 0.05 ? t * 0.85 : 0;
  const fMinX = o.minX + inset, fMaxX = o.maxX - inset, fMinZ = o.minZ + inset, fMaxZ = o.maxZ - inset;
  const floorCol = col(o.floorColor);
  c.floorSlab(fMinX, fMaxX, fMinZ, fMaxZ, o.baseY, o.floorThickness ?? 0.3, floorCol);
  if (o.plankFloor) plankFloor(c, fMinX, fMaxX, fMinZ, fMaxZ, o.baseY, floorCol, o.plankWidth ?? 1.1);

  const rects = continuous
    ? {
      north: { minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.minZ + t },
      south: { minX: o.minX, maxX: o.maxX, minZ: o.maxZ - t, maxZ: o.maxZ },
      west: { minX: o.minX, maxX: o.minX + t, minZ: o.minZ, maxZ: o.maxZ },
      east: { minX: o.maxX - t, maxX: o.maxX, minZ: o.minZ, maxZ: o.maxZ },
    }
    : {
      north: { minX: o.minX, maxX: o.maxX, minZ: o.minZ, maxZ: o.minZ + t },
      south: { minX: o.minX, maxX: o.maxX, minZ: o.maxZ - t, maxZ: o.maxZ },
      west: { minX: o.minX, maxX: o.minX + t, minZ: o.minZ + t, maxZ: o.maxZ - t },
      east: { minX: o.maxX - t, maxX: o.maxX, minZ: o.minZ + t, maxZ: o.maxZ - t },
    };

  const doorGaps = new Map<string, DoorGap>();

  for (const side of ["north", "south", "west", "east"] as const) {
    const rect = rects[side];
    const { axis, nx, nz } = SIDES[side];
    const openings = (o.openings ?? []).filter((op) => op.side === side);
    const door = openings.find((op) => op.kind === "door");

    if (door) {
      const half = (door.w ?? 1.4) / 2, dh = door.h ?? 2.3;
      doorway(c, axis, rect, door.at, half, dh, o.baseY, top, wallCol);
      doorGaps.set(side, { at: door.at, half, h: dh });
    } else {
      c.wall(rect.minX, rect.maxX, rect.minZ, rect.maxZ, o.baseY, o.wallH, wallCol);
    }

    for (const win of openings) {
      if (win.kind !== "window") continue;
      const faceCoord =
        side === "north" ? rect.minZ : side === "south" ? rect.maxZ
        : side === "west" ? rect.minX : rect.maxX;
      windowPane(c, axis, win.at, faceCoord, nx, nz, o.baseY + (win.cy ?? 1.8), {
        w: win.w, h: win.h,
      });
    }
  }

  if (o.wallTreatment === "logs") {
    logTreatment(c, o, doorGaps, o.logColor ? col(o.logColor) : wallCol);
  }
}

/** Run a structure document against a build ctx. Bound into a StructureSpec by
 *  src/structure/index.ts (`interpretStructure` per placed doc). `params`
 *  overrides the doc's declared param defaults (per-placement sizing) —
 *  expressions resolve to concrete numbers HERE, so everything below sees a
 *  plain literal doc. */
export function interpretStructure(c: StructureBuildCtx, doc: StructureDoc, params?: Record<string, number>): void {
  doc = resolveStructureDoc(doc, params);
  const palette = resolvePalette(doc.palette ?? {});
  const col = (k: string) => colorOf(k, palette);

  /** Architecture layer for Builder solo-toggles / inspect --png coloring. */
  const layerOf = (op: StructureOp): string => {
    switch (op.op) {
      case "base": case "plinth": return "base";
      case "mass": case "room": case "wall": case "floor": case "ceiling":
      case "staircase": case "window": case "collide": case "part": return "mass";
      case "gableRoof": case "chimney": case "crenellate": case "tower": return "roof";
      case "furnish": case "light": case "dot": return "furnish";
      default: return "mass";
    }
  };

  for (let opIx = 0; opIx < doc.ops.length; opIx++) {
    const op = doc.ops[opIx]!;
    // Provenance for the studio's editable preview: meshes this op emits carry
    // its index. Indices are RESOLVED-doc positions — the Builder pre-resolves
    // (src/structure/edit.ts resolveForEdit) so it can map them back to
    // authored ops. No-op for the game/server (consolidation discards meshes).
    c.tagOp?.(opIx);
    const run = () => {
      switch (op.op) {
        case "mass":
          mass(c, col, op);
          break;
        case "room":
          room(c, col, op);
          break;
        case "wall":
          c.wall(op.minX, op.maxX, op.minZ, op.maxZ, op.baseY, op.height, col(op.color),
            op.collide === false ? { collide: false } : undefined);
          break;
        case "floor":
          c.floorSlab(op.minX, op.maxX, op.minZ, op.maxZ, op.y, op.thickness, col(op.color), op.surfaceY);
          break;
        case "ceiling":
          c.ceiling(op.minX, op.maxX, op.minZ, op.maxZ, op.y, op.thickness, col(op.color));
          break;
        case "staircase":
          c.staircase(op.minX, op.maxX, op.minZ, op.maxZ, op.yLow, op.yHigh, op.axis, op.lowAt, col(op.color), op.steps);
          break;
        case "gableRoof":
          gableRoof(c, col, op);
          break;
        case "base":
          base(c, col, op);
          break;
        case "plinth":
          plinth(c, col, op);
          break;
        case "chimney":
          chimney(c, col, op);
          break;
        case "crenellate":
          c.crenellate(op.minX, op.maxX, op.minZ, op.maxZ, op.topY, col(op.color), op.axis);
          break;
        case "tower":
          c.tower(op.cx, op.cz, op.r, op.height, col(op.color), col(op.roofColor));
          break;
        case "window":
          windowPane(c, op.axis, op.along, op.faceCoord, op.nx, op.nz, op.cy, {
            w: op.w, h: op.h,
            frame: op.frame ? col(op.frame) : undefined,
            glass: op.glass ? col(op.glass) : undefined,
            style: op.style,
          });
          break;
        case "furnish": {
          const piece = FURNITURE[op.piece];
          if (!piece) throw new Error(`structure "${doc.id}": unknown furniture "${op.piece}"`);
          piece(c, op.x, op.z, op.args ?? {});
          break;
        }
        case "light":
          c.light(op.x, op.y, op.z);
          break;
        case "dot":
          c.dot(op.x, op.z, op.color ? col(op.color) : parseHex(doc.mapColor)!);
          break;
        case "collide":
          c.collideBox(op.minX, op.maxX, op.minZ, op.maxZ, op.minY, op.maxY);
          break;
        case "part":
          interpretParts(c, op.parts as Part[], palette, { root: [0, 0, 0] });
          break;
      }
    };
    // Nested layer() inside curveGableRoof can override (fill/trim).
    if (c.layer) c.layer(layerOf(op), run);
    else run();
  }

  // Auto minimap footprint ring from the union of the doc's room AABBs — saves
  // hand-authoring a `dot` loop. (ops are already resolved to numbers here.)
  if (doc.footprintRing) {
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (const op of doc.ops) {
      if (op.op !== "room" && op.op !== "mass") continue;
      minX = Math.min(minX, op.minX); maxX = Math.max(maxX, op.maxX);
      minZ = Math.min(minZ, op.minZ); maxZ = Math.max(maxZ, op.maxZ);
    }
    if (minX < maxX) {
      const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
      const rx = (maxX - minX) / 2 + 0.4, rz = (maxZ - minZ) / 2 + 0.4;
      const ringCol = parseHex(doc.mapColor)!;
      const n = Math.round(20 + (rx + rz));
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        c.dot(cx + Math.cos(a) * rx, cz + Math.sin(a) * rz, ringCol);
      }
    }
  }
}
